import React, { Suspense, lazy } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

// Layouts
const AdminLayout = lazy(() => import('../layouts/AdminLayout'));
const ClientLayout = lazy(() => import('../layouts/ClientLayout'));
const VendorLayout = lazy(() => import('../layouts/VendorLayout'));

// Route modules
const AdminRoutes = lazy(() => import('./AdminRoutes'));
const ClientRoutes = lazy(() => import('./ClientRoutes'));
const VendorRoutes = lazy(() => import('./VendorRoutes'));

// Public pages
const Login = lazy(() => import('../Components/Login/Login'));
const ForgotPassword = lazy(() => import('../Components/Login/ForgotPassword'));
const ResetPassword = lazy(() => import('../Components/Login/ResetPassword'));
const ContactAdministrator = lazy(() => import('../Components/Login/ContactAdministrator'));
const Signup = lazy(() => import('../Components/Login/Signup'));

const LoadingFallback = () => (
  <div className="flex items-center justify-center h-screen w-full bg-white">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
  </div>
);

// Smart redirect for authenticated users landing on "/"
const RootRedirect = () => {
  const { isAuthenticated, userType, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) return <LoadingFallback />;
  if (!isAuthenticated) return <Login />;

  const role = userType?.toLowerCase();
  if (role === 'vendor') {
    const searchParams = new URLSearchParams(location.search);
    const returnTo = searchParams.get('returnTo');
    // Security: Only allow internal vendor paths, prevent open redirects
    if (returnTo && returnTo.startsWith('/vendor/') && !returnTo.startsWith('//')) {
      return <Navigate to={returnTo} replace />;
    }
    return <Navigate to="/vendor/dashboard" replace />;
  }
  return <Navigate to="/dashboard" replace />;
};

// Route guard + layout selector for shared admin/client paths
const MainAppContent = () => {
  const { isAuthenticated, userType, isLoading } = useAuth();

  if (isLoading) return <LoadingFallback />;
  if (!isAuthenticated) return <Navigate to="/" replace />;

  const role = userType?.toLowerCase();

  // Vendor should not be on these routes
  if (role === 'vendor') return <Navigate to="/vendor/dashboard" replace />;

  if (role === 'admin' || role === 'superadmin') {
    return (
      <AdminLayout>
        <AdminRoutes />
      </AdminLayout>
    );
  }

  // Default to client layout
  return (
    <ClientLayout>
      <ClientRoutes />
    </ClientLayout>
  );
};

// Vendor route guard + layout
const VendorAppContent = () => {
  const { isAuthenticated, userType, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) return <LoadingFallback />;
  if (!isAuthenticated) {
    const returnPath = location.pathname + location.search;
    return <Navigate to={`/?returnTo=${encodeURIComponent(returnPath)}`} replace />;
  }

  const role = userType?.toLowerCase();
  if (role !== 'vendor') {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <VendorLayout>
      <VendorRoutes />
    </VendorLayout>
  );
};

const AppRouter = () => {
  return (
    <Suspense fallback={<LoadingFallback />}>
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={<RootRedirect />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/contact-admin" element={<ContactAdministrator />} />

        {/* Vendor Routes (isolated under /vendor/*) */}
        <Route path="/vendor/*" element={<VendorAppContent />} />

        {/* Admin & Client Routes (share top-level paths, layout determined by role) */}
        <Route path="/*" element={<MainAppContent />} />
      </Routes>
    </Suspense>
  );
};

export default AppRouter;
